import { FISH_FILTER_TERM_ORDER } from "./constants.js";
import { isPlainObject } from "./shared.js";
import {
  isTradeNpcLayerId,
  TRADE_NPC_LAYER_ID,
} from "../map-trade-npc-contract.js";

export const SEARCH_ITEM_SOURCE_KINDS = Object.freeze([
  "rod",
  "zone",
  "hotspot",
  "harpoon",
  "ice_fishing",
]);

const SEARCH_ITEM_SOURCE_KIND_SET = new Set(SEARCH_ITEM_SOURCE_KINDS);
const SEARCH_STATUS_TERMS = new Set(["favourite", "missing", "missing-titles"]);
const SEARCH_GRADE_TERMS = new Set(["red", "yellow", "blue", "green", "white"]);
const BARE_FILTER_PREFIX_MIN_LENGTH = 3;

export function normalizeFishFilterTerm(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (
    normalized === "favourite" ||
    normalized === "favourites" ||
    normalized === "favorite" ||
    normalized === "favorites"
  ) {
    return "favourite";
  }
  if (
    normalized === "missing" ||
    normalized === "uncaught" ||
    normalized === "not caught" ||
    normalized === "not yet caught"
  ) {
    return "missing";
  }
  if (normalized === "missing-titles") return "missing-titles";
  if (normalized === "red" || normalized === "prize") {
    return "red";
  }
  if (normalized === "yellow" || normalized === "rare") {
    return "yellow";
  }
  if (
    normalized === "blue" ||
    normalized === "highquality" ||
    normalized === "high_quality" ||
    normalized === "high-quality"
  ) {
    return "blue";
  }
  if (normalized === "green" || normalized === "general") {
    return "green";
  }
  if (normalized === "white" || normalized === "trash") {
    return "white";
  }
  return "";
}

export function normalizeFishFilterPrefixTerm(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized.length < BARE_FILTER_PREFIX_MIN_LENGTH) {
    return "";
  }
  const matches = FISH_FILTER_TERM_ORDER.filter((term) => term.startsWith(normalized));
  return matches.length && matches.every(term => term === matches[0] || term.startsWith(`${matches[0]}-`)) ? matches[0] : "";
}

export function normalizeSearchStatusTerm(value) {
  const normalized = normalizeFishFilterTerm(value);
  return SEARCH_STATUS_TERMS.has(normalized) ? normalized : "";
}

export function normalizeSearchGradeTerm(value) {
  const normalized = normalizeFishFilterTerm(value);
  return SEARCH_GRADE_TERMS.has(normalized) ? normalized : "";
}

export function normalizeFishFilterTerms(values) {
  const selected = new Set();
  for (const value of Array.isArray(values) ? values : []) {
    const normalized = normalizeFishFilterTerm(value);
    if (normalized) {
      selected.add(normalized);
    }
  }
  return FISH_FILTER_TERM_ORDER.filter((term) => selected.has(term));
}

export function normalizePatchBound(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "from" || normalized === "start" || normalized === "since" || normalized === "after") {
    return "from";
  }
  if (
    normalized === "to" ||
    normalized === "until" ||
    normalized === "end" ||
    normalized === "through" ||
    normalized === "before"
  ) {
    return "to";
  }
  return "";
}

export function normalizePatchId(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function normalizePositiveIntegerList(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = Number.parseInt(value, 10);
    if (!Number.isInteger(normalized) || normalized <= 0 || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }
  return next.sort((left, right) => left - right);
}

function normalizePositiveInteger(value) {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }
  const normalized = /^0x[0-9a-f]+$/i.test(raw)
    ? Number.parseInt(raw.slice(2), 16)
    : Number.parseInt(raw, 10);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
}

export function normalizeSourceKind(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) {
    return "";
  }
  if (normalized === "ice-fishing") {
    return "ice_fishing";
  }
  return normalized;
}

export function isKnownSourceKind(value) {
  const normalized = normalizeSourceKind(value);
  return Boolean(normalized && SEARCH_ITEM_SOURCE_KIND_SET.has(normalized));
}

export function normalizeLayerFactId(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

export function normalizeLayerFilterId(value) {
  const normalized = String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === "zone" || normalized === "zone_mask" || normalized === "fishing_zones") {
    return "zones";
  }
  if (normalized === "hotspot") {
    return "hotspots";
  }
  if (normalized === "bookmark") {
    return "bookmarks";
  }
  return normalized || "";
}

function normalizeLayerPredicatePublicId(value) {
  const normalized = String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return normalized || "";
}

const LAYER_FACT_CONDITION_OPERATOR_ALIASES = Object.freeze({
  "=": "eq",
  "==": "eq",
  "!=": "ne",
  ">": "gt",
  ">=": "gte",
  "<": "lt",
  "<=": "lte",
  "~=": "eq-ci",
  "~": "contains-ci",
});

const LAYER_FACT_CONDITION_OPERATORS = new Set([
  "exists",
  "eq",
  "eq-ci",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains-ci",
]);

export function normalizeLayerFactConditionOperator(value) {
  const raw = String(value ?? "eq").trim().toLowerCase();
  const normalized = LAYER_FACT_CONDITION_OPERATOR_ALIASES[raw] || raw;
  return LAYER_FACT_CONDITION_OPERATORS.has(normalized) ? normalized : "";
}

function normalizeLayerFactConditionValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (value == null) {
    return undefined;
  }
  const normalized = String(value).trim();
  return normalized === "" ? undefined : normalized;
}

function normalizeLayerFactConditionTolerance(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? value : undefined;
  }
  if (typeof value !== "string") {
    return undefined;
  }
  const normalized = value.trim();
  if (!normalized) {
    return undefined;
  }
  const tolerance = Number(normalized);
  return Number.isFinite(tolerance) && tolerance >= 0 ? tolerance : undefined;
}

export function normalizeLayerFactCondition(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }
  const path = String(raw.path ?? raw.field ?? raw.key ?? "").trim();
  const op = normalizeLayerFactConditionOperator(raw.op ?? raw.operator);
  if (!path || !op) {
    return null;
  }
  const condition = { path, op };
  if (op !== "exists") {
    const value = normalizeLayerFactConditionValue(raw.value);
    if (value === undefined) {
      return null;
    }
    condition.value = value;
  }
  const tolerance = normalizeLayerFactConditionTolerance(raw.tolerance);
  if (tolerance !== undefined) {
    condition.tolerance = tolerance;
  }
  return condition;
}

function layerFactConditionKeyObject(condition) {
  return {
    path: condition.path,
    op: condition.op,
    ...(Object.prototype.hasOwnProperty.call(condition, "value")
      ? { value: condition.value }
      : {}),
    ...(Object.prototype.hasOwnProperty.call(condition, "tolerance")
      ? { tolerance: condition.tolerance }
      : {}),
  };
}

export function layerFactConditionKey(condition) {
  const normalized = normalizeLayerFactCondition(condition);
  return normalized ? JSON.stringify(layerFactConditionKeyObject(normalized)) : "";
}

export function normalizeLayerFactConditions(values) {
  const normalized = [];
  const seen = new Set();
  for (const value of Array.isArray(values) ? values : []) {
    const condition = normalizeLayerFactCondition(value);
    const key = layerFactConditionKey(condition);
    if (!condition || !key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    normalized.push(condition);
  }
  return normalized.sort((left, right) =>
    layerFactConditionKey(left).localeCompare(layerFactConditionKey(right))
  );
}

export function normalizeLayerElementFactTerm(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }
  const layerId = normalizeLayerFactId(raw.layerId ?? raw.layer ?? raw.sourceLayer);
  const factId = normalizeLayerFactId(raw.factId ?? raw.fact ?? raw.fieldId ?? raw.value ?? raw.id);
  if (!layerId || !factId) {
    return null;
  }
  const conditions = normalizeLayerFactConditions(raw.conditions ?? raw.where);
  return conditions.length
    ? { kind: "layer-fact", layerId, factId, conditions }
    : { kind: "layer-fact", layerId, factId };
}

function tradeNpcKey(value) {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  const raw = String(value ?? "").trim();
  if (!/^\d+$/.test(raw)) {
    return null;
  }
  const normalized = Number.parseInt(raw, 10);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
}

export function tradeNpcSearchTermForKey(value) {
  const npcKey = tradeNpcKey(value);
  return npcKey == null
    ? null
    : {
        kind: "layer-fact",
        layerId: TRADE_NPC_LAYER_ID,
        factId: "npc_key",
        conditions: [{ path: "$", op: "eq", value: String(npcKey) }],
      };
}

export function tradeNpcKeyFromSearchTerm(term) {
  const normalized = normalizeLayerElementFactTerm(term);
  if (
    !isTradeNpcLayerId(normalized?.layerId) ||
    normalized.factId !== "npc_key" ||
    normalized.conditions?.length !== 1
  ) {
    return null;
  }
  const condition = normalized.conditions[0];
  if (
    !["$", "value"].includes(condition.path) ||
    condition.op !== "eq" ||
    Object.prototype.hasOwnProperty.call(condition, "tolerance")
  ) {
    return null;
  }
  return tradeNpcKey(condition.value);
}

const LAYER_FILTER_COMPARE_OPERATOR_ALIASES = Object.freeze({
  "=": "eq",
  "==": "eq",
  "!=": "ne",
  ">": "gt",
  ">=": "gte",
  "<": "lt",
  "<=": "lte",
  "~=": "eq-ci",
});

const LAYER_FILTER_COMPARE_OPERATORS = new Set([
  "eq",
  "eq-ci",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
]);

export function normalizeLayerFilterCompareOperator(value) {
  const raw = String(value ?? "eq").trim().toLowerCase();
  const normalized = LAYER_FILTER_COMPARE_OPERATOR_ALIASES[raw] || raw;
  return LAYER_FILTER_COMPARE_OPERATORS.has(normalized) ? normalized : "";
}

function normalizeLayerFilterPercentValue(value) {
  if (isPlainObject(value)) {
    const basisPoints = Number.parseInt(value.basisPoints ?? value.basis_points, 10);
    if (Number.isInteger(basisPoints) && basisPoints >= 0) {
      return { kind: "percent", basisPoints };
    }
  }
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0
      ? { kind: "percent", basisPoints: Math.round(value * 100) }
      : null;
  }
  const raw = String(value ?? "").trim();
  const match = raw.match(/^(\d+)(?:\.(\d{1,2}))?%?$/);
  if (!match) {
    return null;
  }
  const whole = Number.parseInt(match[1], 10);
  const fraction = Number.parseInt((match[2] || "").padEnd(2, "0"), 10) || 0;
  return Number.isInteger(whole) ? { kind: "percent", basisPoints: whole * 100 + fraction } : null;
}

function normalizeLayerFilterRateComparison(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }
  const op = normalizeLayerFilterCompareOperator(raw.op ?? raw.operator ?? raw.compare);
  const value = normalizeLayerFilterPercentValue(raw.value ?? raw.percent ?? raw.rate);
  return op && value ? { op, value } : null;
}

function normalizeLayerFilterTypedValue(value) {
  if (isPlainObject(value)) {
    const kind = String(value.kind ?? value.type ?? "").trim().toLowerCase();
    if (kind === "number" || kind === "integer") {
      const parsed = Number(value.value);
      return Number.isFinite(parsed) ? { kind: "number", value: parsed } : null;
    }
    if (kind === "boolean" || kind === "bool") {
      if (typeof value.value === "boolean") {
        return { kind: "boolean", value: value.value };
      }
      if (/^(true|false)$/i.test(String(value.value ?? "").trim())) {
        return { kind: "boolean", value: String(value.value).trim().toLowerCase() === "true" };
      }
      return null;
    }
    if (kind === "percent") {
      const percent = normalizeLayerFilterPercentValue(value);
      return percent ? { kind: "percent", basisPoints: percent.basisPoints } : null;
    }
    const text = String(value.value ?? "").trim();
    return text ? { kind: "text", value: text } : null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? { kind: "number", value } : null;
  }
  if (typeof value === "boolean") {
    return { kind: "boolean", value };
  }
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }
  if (/^(true|false)$/i.test(raw)) {
    return { kind: "boolean", value: raw.toLowerCase() === "true" };
  }
  if (/^-?\d+(?:\.\d+)?$/.test(raw)) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? { kind: "number", value: parsed } : null;
  }
  const percent = normalizeLayerFilterPercentValue(raw);
  if (percent && raw.endsWith("%")) {
    return percent;
  }
  return { kind: "text", value: raw };
}

function normalizeLayerFilterSubject(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }
  const kind = String(raw.kind ?? raw.type ?? "").trim().toLowerCase().replace(/-/g, "_");
  if (kind === "fish") {
    const fishId = normalizePositiveInteger(raw.fishId ?? raw.fish_id ?? raw.itemId ?? raw.item_id ?? raw.value ?? raw.id);
    return fishId != null ? { kind: "fish", fishId } : null;
  }
  if (kind === "item") {
    const itemId = normalizePositiveInteger(raw.itemId ?? raw.item_id ?? raw.fishId ?? raw.fish_id ?? raw.value ?? raw.id);
    return itemId != null ? { kind: "item", itemId } : null;
  }
  if (kind === "item_group" || kind === "itemgroup") {
    const itemIds = normalizePositiveIntegerList(raw.itemIds ?? raw.item_ids);
    return itemIds.length ? { kind: "item-group", itemIds } : null;
  }
  if (kind === "zone") {
    const zoneRgb = normalizePositiveInteger(raw.zoneRgb ?? raw.zone_rgb ?? raw.value ?? raw.id);
    return zoneRgb != null ? { kind: "zone", zoneRgb } : null;
  }
  const subjectKind = normalizeLayerPredicatePublicId(kind || raw.subjectKind);
  return subjectKind ? { kind: subjectKind } : null;
}

function layerFilterSubjectKey(subject) {
  const normalized = normalizeLayerFilterSubject(subject);
  if (!normalized) {
    return "";
  }
  if (normalized.kind === "fish") {
    return `fish:${normalized.fishId}`;
  }
  if (normalized.kind === "item") {
    return `item:${normalized.itemId}`;
  }
  if (normalized.kind === "item-group") {
    return `items:${normalized.itemIds.join(",")}`;
  }
  if (normalized.kind === "zone") {
    return `zone:${normalized.zoneRgb}`;
  }
  return normalized.kind;
}

function normalizeLayerFilterPredicate(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }
  const kind = String(raw.kind ?? raw.type ?? "").trim().toLowerCase().replace(/_/g, "-");
  if (kind === "bool" || kind === "group") {
    const op = String(raw.op ?? raw.operator ?? "").trim().toLowerCase() === "and" ? "and" : "or";
    const children = (Array.isArray(raw.children) ? raw.children : [])
      .map(normalizeLayerFilterPredicate)
      .filter(Boolean);
    if (!children.length) {
      return null;
    }
    const predicate = { kind: "bool", op, children };
    if (raw.negated === true) {
      predicate.negated = true;
    }
    return predicate;
  }
  if (kind === "subject-presence" || kind === "presence") {
    const subject = normalizeLayerFilterSubject(raw.subject ?? raw.value);
    return subject ? { kind: "subject-presence", subject } : null;
  }
  if (kind === "membership") {
    const subject = normalizeLayerFilterSubject(raw.subject);
    const group = normalizeLayerPredicatePublicId(raw.group ?? raw.groupId ?? raw.group_id);
    const membershipOp = String(raw.op ?? raw.operator ?? "in").trim().toLowerCase().replace(/_/g, "-");
    if (!subject || !group || (membershipOp !== "in" && membershipOp !== "not-in")) {
      return null;
    }
    const rate = normalizeLayerFilterRateComparison(raw.rate ?? raw.comparison);
    return rate
      ? { kind: "membership", subject, group, op: membershipOp, rate }
      : { kind: "membership", subject, group, op: membershipOp };
  }
  if (kind === "group-rate") {
    const group = normalizeLayerPredicatePublicId(raw.group ?? raw.groupId ?? raw.group_id);
    const comparison = normalizeLayerFilterRateComparison(raw.comparison ?? raw.rate);
    return group && comparison ? { kind: "group-rate", group, comparison } : null;
  }
  if (kind === "property-compare" || kind === "field-compare") {
    const field = normalizeLayerPredicatePublicId(raw.field ?? raw.fieldId ?? raw.field_id ?? raw.key);
    const op = normalizeLayerFilterCompareOperator(raw.op ?? raw.operator);
    const value = normalizeLayerFilterTypedValue(raw.value);
    return field && op && value ? { kind: "property-compare", field, op, value } : null;
  }
  if (kind === "relation") {
    const relation = normalizeLayerPredicatePublicId(raw.relation ?? raw.op ?? raw.operator);
    const target = normalizeLayerFilterSubject(raw.target ?? raw.subject);
    return relation && target ? { kind: "relation", relation, target } : null;
  }
  return null;
}

export function normalizeLayerFilterTerm(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }
  const layerId = normalizeLayerFilterId(raw.layerId ?? raw.layer ?? raw.id);
  const predicate = normalizeLayerFilterPredicate(raw.predicate ?? raw.where ?? raw.value);
  return layerId && predicate ? { kind: "layer-filter", layerId, predicate } : null;
}

function layerFilterRateComparisonKey(comparison) {
  const normalized = normalizeLayerFilterRateComparison(comparison);
  return normalized ? `${normalized.op}:${normalized.value.basisPoints}` : "";
}

function layerFilterTypedValueKey(value) {
  const normalized = normalizeLayerFilterTypedValue(value);
  if (!normalized) {
    return "";
  }
  if (normalized.kind === "percent") {
    return `percent:${normalized.basisPoints}`;
  }
  return `${normalized.kind}:${String(normalized.value)}`;
}

function layerFilterPredicateKey(predicate) {
  const normalized = normalizeLayerFilterPredicate(predicate);
  if (!normalized) {
    return "";
  }
  if (normalized.kind === "bool") {
    const negated = normalized.negated ? ":not" : "";
    return `bool:${normalized.op}${negated}(${normalized.children.map(layerFilterPredicateKey).join("|")})`;
  }
  if (normalized.kind === "subject-presence") {
    return `presence:${layerFilterSubjectKey(normalized.subject)}`;
  }
  if (normalized.kind === "membership") {
    const rate = normalized.rate ? `:${layerFilterRateComparisonKey(normalized.rate)}` : "";
    return `membership:${layerFilterSubjectKey(normalized.subject)}:${normalized.op}:${normalized.group}${rate}`;
  }
  if (normalized.kind === "group-rate") {
    return `group-rate:${normalized.group}:${layerFilterRateComparisonKey(normalized.comparison)}`;
  }
  if (normalized.kind === "property-compare") {
    return `property:${normalized.field}:${normalized.op}:${layerFilterTypedValueKey(normalized.value)}`;
  }
  if (normalized.kind === "relation") {
    return `relation:${normalized.relation}:${layerFilterSubjectKey(normalized.target)}`;
  }
  return "";
}

export function normalizeSourceKinds(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = normalizeSourceKind(value);
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }
  return next.sort();
}

export function normalizeSearchTerm(raw) {
  if (!isPlainObject(raw)) {
    return null;
  }
  const kind = String(raw.kind ?? raw.type ?? "").trim().toLowerCase();
  if (kind === "title") {
    return Number.isInteger(raw.titleId) && raw.titleId > 0 && raw.titleId <= 2_147_483_647
      ? { kind: "title", titleId: raw.titleId } : null;
  }
  if (kind === "fishing-contest") {
    return { kind: "fishing-contest" };
  }
  if (kind === "patch-bound" || kind === "patch") {
    const bound = normalizePatchBound(raw.bound ?? raw.patchBound ?? raw.side);
    const patchId = normalizePatchId(raw.patchId ?? raw.value ?? raw.id);
    if (!bound) {
      return null;
    }
    return patchId
      ? { kind: "patch-bound", bound, patchId }
      : { kind: "patch-bound", bound };
  }
  if (kind === "status") {
    const status = normalizeSearchStatusTerm(raw.status ?? raw.term ?? raw.value ?? raw.id);
    return status ? { kind: "status", status } : null;
  }
  if (kind === "grade") {
    const grade = normalizeSearchGradeTerm(raw.grade ?? raw.term ?? raw.value ?? raw.id);
    return grade ? { kind: "grade", grade } : null;
  }
  if (kind === "fish") {
    const fishId = normalizePositiveInteger(raw.fishId ?? raw.itemId ?? raw.value ?? raw.id);
    return fishId != null ? { kind: "fish", fishId } : null;
  }
  if (kind === "item") {
    const itemId = normalizePositiveInteger(raw.itemId ?? raw.fishId ?? raw.value ?? raw.id);
    if (itemId == null) {
      return null;
    }
    const sourceKinds = normalizeSourceKinds(raw.sourceKinds);
    return sourceKinds.length ? { kind: "item", itemId, sourceKinds } : { kind: "item", itemId };
  }
  if (kind === "item-group" || kind === "itemgroup") {
    const itemIds = normalizePositiveIntegerList(raw.itemIds ?? raw.item_ids);
    if (!itemIds.length) {
      return null;
    }
    const sourceKinds = normalizeSourceKinds(raw.sourceKinds);
    return sourceKinds.length
      ? { kind: "item-group", itemIds, sourceKinds }
      : { kind: "item-group", itemIds };
  }
  if (kind === "zone") {
    const zoneRgb = normalizePositiveInteger(raw.zoneRgb ?? raw.fieldId ?? raw.value ?? raw.id);
    return zoneRgb != null ? { kind: "zone", zoneRgb } : null;
  }
  if (kind === "semantic") {
    const layerId = String(raw.layerId ?? "").trim();
    const fieldId = Number.parseInt(raw.fieldId, 10);
    if (layerId === "zone_mask") {
      return Number.isInteger(fieldId) && fieldId > 0 ? { kind: "zone", zoneRgb: fieldId } : null;
    }
    if (!layerId || !Number.isInteger(fieldId) || fieldId <= 0) {
      return null;
    }
    return { kind: "semantic", layerId, fieldId };
  }
  if (kind === "layer-fact" || kind === "fact") {
    return normalizeLayerElementFactTerm(raw);
  }
  if (kind === "layer-filter" || kind === "filter") {
    return normalizeLayerFilterTerm(raw);
  }
  return null;
}

export function searchTermKey(term) {
  const normalizedTerm = normalizeSearchTerm(term);
  if (!normalizedTerm) {
    return "";
  }
  term = normalizedTerm;
  if (term.kind === "title") return `title:${term.titleId}`;
  if (term.kind === "fishing-contest") {
    return "fishing-contest";
  }
  if (term.kind === "patch-bound") {
    const bound = normalizePatchBound(term.bound);
    const patchId = normalizePatchId(term.patchId);
    return bound ? `patch-bound:${bound}:${patchId || "__pending__"}` : "";
  }
  if (term.kind === "status") {
    const status = normalizeSearchStatusTerm(term.status);
    return status ? `status:${status}` : "";
  }
  if (term.kind === "grade") {
    const grade = normalizeSearchGradeTerm(term.grade);
    return grade ? `grade:${grade}` : "";
  }
  if (term.kind === "fish") {
    const fishId = Number.parseInt(term.fishId, 10);
    return Number.isInteger(fishId) ? `fish:${fishId}` : "";
  }
  if (term.kind === "item") {
    const itemId = Number.parseInt(term.itemId, 10);
    if (!Number.isInteger(itemId)) {
      return "";
    }
    const sourceKinds = normalizeSourceKinds(term.sourceKinds);
    return sourceKinds.length ? `item:${itemId}:${sourceKinds.join(",")}` : `item:${itemId}`;
  }
  if (term.kind === "item-group") {
    const itemIds = normalizePositiveIntegerList(term.itemIds);
    if (!itemIds.length) {
      return "";
    }
    const sourceKinds = normalizeSourceKinds(term.sourceKinds);
    return sourceKinds.length
      ? `item-group:${itemIds.join(",")}:${sourceKinds.join(",")}`
      : `item-group:${itemIds.join(",")}`;
  }
  if (term.kind === "zone") {
    const zoneRgb = Number.parseInt(term.zoneRgb, 10);
    return Number.isInteger(zoneRgb) ? `zone:${zoneRgb}` : "";
  }
  if (term.kind === "semantic") {
    const layerId = String(term.layerId ?? "").trim();
    const fieldId = Number.parseInt(term.fieldId, 10);
    return layerId && Number.isInteger(fieldId) ? `semantic:${layerId}:${fieldId}` : "";
  }
  if (term.kind === "layer-fact") {
    const layerId = normalizeLayerFactId(term.layerId);
    const factId = normalizeLayerFactId(term.factId);
    if (!layerId || !factId) {
      return "";
    }
    const conditions = normalizeLayerFactConditions(term.conditions);
    return conditions.length
      ? `fact:${layerId}:${factId}:${conditions.map(layerFactConditionKey).join(",")}`
      : `fact:${layerId}:${factId}`;
  }
  if (term.kind === "layer-filter") {
    const layerId = normalizeLayerFilterId(term.layerId);
    const predicateKey = layerFilterPredicateKey(term.predicate);
    return layerId && predicateKey ? `filter:${layerId}:${predicateKey}` : "";
  }
  return "";
}

export function normalizeSelectedSearchTerms(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = normalizeSearchTerm(value);
    if (!normalized) {
      continue;
    }
    const key = searchTermKey(normalized);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    next.push(normalized);
  }
  return next;
}
